<template>
  <div class="ledger-page">
    <b-card class="rounded-lg">
      <h4 class="mb-1">
        {{ $t('ledger_payslip_title') }}
      </h4>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!rows.length"
        class="text-center text-muted py-4"
      >
        {{ $t('ledger_payslip_empty') }}
      </div>

      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="rows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(period)="data">
              <b-link :to="showRoute(data.item)">
                {{ periodLabel(data.item) }}
              </b-link>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusBadgeVariant(statusValue(data.item))">
                {{ statusText(data.item) }}
              </b-badge>
            </template>
            <template #cell(net)="data">
              <span class="ledger-num">{{ money(netOf(data.item)) }}</span>
            </template>
          </b-table>
        </div>

        <div class="d-md-none">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-payroll-card"
            @click="$router.push(showRoute(item))"
          >
            <div class="d-flex justify-content-between align-items-center">
              <strong>{{ periodLabel(item) }}</strong>
              <b-badge :variant="statusBadgeVariant(statusValue(item))">
                {{ statusText(item) }}
              </b-badge>
            </div>
            <div class="ledger-num mt-50">
              {{ $t('ledger_payroll_net') }} {{ money(netOf(item)) }}
            </div>
          </article>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BTable, BBadge, BLink, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getMyPayslips } from '@/api/me/payslips'
import {
  listFrom, enumValue, enumLabel, formatLedgerMoney, statusBadgeVariant, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'MyPayslips',
  components: {
    BCard, BTable, BBadge, BLink, BSpinner,
  },
  data() {
    return {
      loading: false,
      rows: [],
    }
  },
  computed: {
    fields() {
      return [
        { key: 'period', label: this.$t('ledger_payroll_period') },
        { key: 'status', label: this.$t('ledger_status') },
        { key: 'net', label: this.$t('ledger_payroll_net') },
      ]
    },
  },
  mounted() {
    this.loadRows()
  },
  methods: {
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    monthName(month) {
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      return new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2020, Number(month) - 1, 1))
    },
    periodLabel(row) {
      return `${this.monthName(row.month)} ${row.year}`
    },
    statusValue(row) {
      return enumValue(row.status)
    },
    statusText(row) {
      return enumLabel(row.status, this.$i18n.locale)
    },
    netOf(row) {
      if (row.totals && row.totals.net != null) return row.totals.net
      if (row.line && row.line.net != null) return row.line.net
      if (row.net != null) return row.net
      return 0
    },
    showRoute(row) {
      return { name: 'me-payslip-view', params: { id: row.payroll_run_id || row.id } }
    },
    async loadRows() {
      this.loading = true
      try {
        const res = await getMyPayslips()
        this.rows = listFrom(res)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-payroll-card {
  border-bottom: 1px solid #eee;
  padding: 0.85rem 0.15rem;
  cursor: pointer;
}
</style>
